
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { ArrowRight, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import PostContent from '@/components/Posts/PostContent';
import BlogHero from '@/components/Blog/BlogHero';
import BlogPostCard from '@/components/Blog/BlogPostCard';
import LocationControls from '@/components/Blog/LocationControls';
import BlogSidebar from '@/components/Blog/BlogSidebar';
import RelatedArticles from '@/components/Blog/RelatedArticles';
import BlogCTA from '@/components/Blog/BlogCTA';
import { useSchema } from '@/hooks/useSchema';
import { generateBreadcrumbSchema, generateFAQSchema } from '@/utils/schema';
import AutoSEO from '@/components/SEO/AutoSEO';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

interface BlogLocation {
  id: string;
  name: string;
  county: string;
  population?: number;
}

const BlogPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [locations, setLocations] = useState<BlogLocation[]>([]);
  const [currentPost, setCurrentPost] = useState<BlogPost | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('raleigh');
  const [loading, setLoading] = useState(true);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');

  // Predefined location buttons to show
  const featuredLocations = [
    'Wilson',
    'Rocky Mount', 
    'Greenville',
    'Goldsboro',
    'Winterville',
    'New Bern'
  ];

  useEffect(() => {
    console.log('Blog useEffect triggered:', { slug, searchParams: searchParams.toString() });
    try {
      // Get location from URL params or default to Raleigh
      const locationParam = searchParams.get('location') || 'raleigh';
      setSelectedLocation(locationParam);
      
      if (slug) {
        console.log('Fetching single post for slug:', slug);
        fetchSinglePost();
      } else {
        console.log('Fetching location-based posts for:', locationParam);
        setCurrentPost(null); // Reset current post when no slug
        fetchLocationBasedPosts(locationParam);
      }
      
      fetchLocations();
    } catch (error) {
      console.error('Error in Blog useEffect:', error);
    }
  }, [slug, searchParams]);

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('service_locations')
        .select('id, name, county')
        .order('name')
        .limit(50);

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchSinglePost = async () => {
    try {
      console.log('Fetching single post with slug:', slug);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, content, excerpt, slug, author, published_date, status, seo_title, seo_description, seo_keywords, categories, tags, created_at, updated_at')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;
      setCurrentPost(data as BlogPost);
    } catch (error) {
      console.error('Error fetching post:', error);
      setCurrentPost(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationBasedPosts = async (locationName: string) => {
    try {
      console.log('Fetching posts for location:', locationName);
      setLoading(true);
      
      // Build a more targeted query with exact location matching
      let query = supabase
        .from('blog_posts')
        .select('id, title, excerpt, slug, author, published_date, seo_keywords, tags, created_at, updated_at')
        .eq('status', 'published')
        .order('published_date', { ascending: false })
        .limit(20); // Limit to 20 posts per location

      // Filter by location with exact matching to prevent Wilson/Wilsonville confusion
      if (locationName !== 'all') {
        const locationCapitalized = locationName.charAt(0).toUpperCase() + locationName.slice(1);
        
        // Use simple ilike for title matching and tags
        query = query.or(`title.ilike.%${locationCapitalized}%,tags.cs.{"${locationName.toLowerCase()}-roofing"}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Additional client-side filtering to ensure exact location matches
      let filteredData = data || [];
      
      if (locationName !== 'all') {
        const locationLower = locationName.toLowerCase();
        const locationCapitalized = locationName.charAt(0).toUpperCase() + locationName.slice(1);
        
        filteredData = (data || []).filter(post => {
          // Extract location from post using exact matching
          const postLocation = extractCityFromPost(post as BlogPost);
          
          // Check if the extracted location exactly matches our target location
          if (postLocation && postLocation.toLowerCase() === locationLower) {
            return true;
          }
          
          // Check tags for exact location match
          if (post.tags && post.tags.includes(`${locationLower}-roofing`)) {
            return true;
          }
          
          // Check title for exact word boundary matches
          const titleWords = post.title.split(/\s+/);
          const hasExactLocationMatch = titleWords.some(word => 
            word.replace(/[^a-zA-Z]/g, '').toLowerCase() === locationLower
          );
          
          return hasExactLocationMatch;
        });
      }
      
      // Cast to BlogPost array since we selected all fields
      const typedPosts = filteredData as BlogPost[];
      
      console.log(`Filtered to ${typedPosts.length} posts for ${locationName}`);
      setPosts(typedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (locationName: string) => {
    setSelectedLocation(locationName);
    setSearchParams({ location: locationName });
    fetchLocationBasedPosts(locationName);
  };

  const handleBackToAllArticles = () => {
    setCurrentPost(null);
    navigate('/blog');
  };

  const extractCityFromPost = (post: BlogPost): string | null => {
    const title = post.title.toLowerCase();
    
    // Look for city patterns in title with exact word boundary matching
    const titleLocationPatterns = [
      // Look for "Location Church" or "Location Roofing" at start with word boundaries
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:Church|Roofing|Roof)\b/i,
      // Look for "in Location, NC" with word boundaries  
      /\bin\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\b,?\s*(?:NC|North Carolina)/i,
      // Look for "Location, NC" with word boundaries
      /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\b,?\s*(?:NC|North Carolina)/i,
    ];

    for (const pattern of titleLocationPatterns) {
      const match = post.title.match(pattern);
      if (match && match[1]) {
        let location = match[1].trim();
        const excludeWords = ['Your', 'The', 'Why', 'When', 'Faith', 'Meets', 'Engineering', 'Homeowners', 'Are', 'Switching'];
        if (!excludeWords.some(word => location.includes(word))) {
          return location;
        }
      }
    }

    // Check tags for location info with exact matching
    if (post.tags) {
      for (const tag of post.tags) {
        if (tag.includes('-roofing') && !tag.includes('roofing-')) {
          const location = tag.replace('-roofing', '').replace(/-/g, ' ');
          return location.charAt(0).toUpperCase() + location.slice(1);
        }
      }
    }

    return null;
  };

  const calculateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  // Filter locations by search query
  const filteredLocations = locationSearchQuery
    ? locations.filter(location => 
        location.name.toLowerCase().includes(locationSearchQuery.toLowerCase()) ||
        location.county.toLowerCase().includes(locationSearchQuery.toLowerCase())
      )
    : locations;

  // Memoized breadcrumbs and schemas for current post
  const postBreadcrumbs = useMemo(() => {
    if (!currentPost) return [] as any[];
    return [
      { name: 'Home', url: 'https://www.carolinatrophyroofs.com' },
      { name: 'Blog', url: 'https://www.carolinatrophyroofs.com/blog' },
      { name: currentPost.title, url: `https://www.carolinatrophyroofs.com/blog/${currentPost.slug}` }
    ];
  }, [currentPost?.id, currentPost?.slug, currentPost?.title]);

  const postSchemas = useMemo(() => {
    if (!currentPost) return [] as any[];
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: currentPost.seo_title || currentPost.title,
        description: currentPost.seo_description || currentPost.excerpt || '',
        image: currentPost.featured_image ? {
          '@type': 'ImageObject',
          url: currentPost.featured_image,
          creditText: 'Carolina Trophy Roofs',
          license: 'https://creativecommons.org/licenses/by/4.0/',
          acquireLicensePage: 'https://www.carolinatrophyroofs.com/contact',
          copyrightNotice: '© Carolina Trophy Roofs. All rights reserved.'
        } : undefined,
        author: { '@type': 'Person', name: currentPost.author },
        publisher: {
          '@type': 'Organization',
          name: 'Carolina Trophy Roofs',
          logo: { 
            '@type': 'ImageObject', 
            url: 'https://www.carolinatrophyroofs.com/logo.png',
            creditText: 'Carolina Trophy Roofs',
            license: 'https://creativecommons.org/licenses/by/4.0/',
            acquireLicensePage: 'https://www.carolinatrophyroofs.com/contact',
            copyrightNotice: '© Carolina Trophy Roofs. All rights reserved.'
          }
        },
        datePublished: currentPost.published_date || currentPost.created_at || '',
        dateModified: currentPost.updated_at || currentPost.created_at || '',
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.carolinatrophyroofs.com/blog/${currentPost.slug}` },
        keywords: currentPost.seo_keywords?.join(', ') || currentPost.tags?.join(', ') || ''
      },
      generateBreadcrumbSchema(postBreadcrumbs as any)
    ];
  }, [currentPost?.id, currentPost?.slug, currentPost?.title, currentPost?.seo_title]);

  useSchema({ type: 'multiple', schemas: postSchemas as any });

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto py-16 px-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-trophy-gold"></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Display single blog post
  if (currentPost) {
    const postCity = extractCityFromPost(currentPost);
    
    // Simplified article schema
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": currentPost.seo_title || currentPost.title,
      "description": currentPost.seo_description || currentPost.excerpt || "",
      "image": currentPost.featured_image,
      "author": {
        "@type": "Person",
        "name": currentPost.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Carolina Trophy Roofs",
        "url": "https://www.carolinatrophyroofs.com"
      },
      "datePublished": currentPost.published_date || currentPost.created_at,
      "dateModified": currentPost.updated_at || currentPost.created_at,
      "url": `https://www.carolinatrophyroofs.com/blog/${currentPost.slug}`,
      "keywords": currentPost.seo_keywords?.join(', ') || currentPost.tags?.join(', ') || "",
      "articleSection": currentPost.categories?.join(', ') || "Roofing"
    };
    
    return (
      <Layout>
        <AutoSEO
          pageType="blog"
          articleData={{
            author: currentPost.author,
            publishedTime: currentPost.published_date || currentPost.created_at || '',
            modifiedTime: currentPost.updated_at || currentPost.created_at || ''
          }}
          customTitle={currentPost.seo_title || `${currentPost.title} | Carolina Trophy Roofs`}
          customDescription={currentPost.seo_description || currentPost.excerpt || `${currentPost.title} - Expert roofing insights from Carolina Trophy Roofs.`}
          customKeywords={currentPost.seo_keywords || currentPost.tags || []}
        />
        
        {/* Single Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <div className="container mx-auto py-8 md:py-16 px-4">
          <Button variant="outline" onClick={handleBackToAllArticles} className="flex items-center gap-2 mb-8 border-brand-charcoal-gray text-brand-charcoal-gray hover:bg-brand-charcoal-gray hover:text-white">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Back to all articles
          </Button>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="col-span-2">
              <PostContent 
                post={currentPost} 
                readTime={calculateReadTime(currentPost.content)} 
              />
              
              <div className="border-t border-gray-200 pt-8 mt-12">
                <h3 className="text-xl font-bold mb-6 text-brand-charcoal-gray">Share this article</h3>
                <div className="flex space-x-4">
                  <Button variant="outline" size="sm" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-white">Facebook</Button>
                  <Button variant="outline" size="sm" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-white">Twitter</Button>
                  <Button variant="outline" size="sm" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-white">LinkedIn</Button>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-center lg:block">
              <RelatedArticles posts={posts} />
              <BlogCTA location={postCity || 'Eastern NC'} />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Display blog index page with location filtering
  // Simplified blog index schema
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Carolina Trophy Roofs Blog",
    "description": "Expert roofing insights, tips, and location-specific guides for Eastern North Carolina homeowners.",
    "url": "https://www.carolinatrophyroofs.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Carolina Trophy Roofs",
      "url": "https://www.carolinatrophyroofs.com"
    },
    "blogPost": posts.map((post, index) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt || post.seo_description || "",
      "url": `https://www.carolinatrophyroofs.com/blog/${post.slug}`,
      "datePublished": post.published_date || post.created_at,
      "dateModified": post.updated_at || post.created_at,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "image": post.featured_image,
      "keywords": post.tags?.join(', ') || ""
    }))
  };

  return (
    <Layout>
      <AutoSEO pageType="blog" />
      
      {/* Single Blog Schema */}
      <script type="application/ld+json">
        {JSON.stringify(blogSchema)}
      </script>
      
      <BlogHero selectedLocation={selectedLocation} />

      {/* Blog Content */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Content */}
            <div className="w-full lg:w-2/3">
              <LocationControls 
                selectedLocation={selectedLocation}
                featuredLocations={featuredLocations}
                onLocationChange={handleLocationChange}
              />
              
              {/* Posts Display */}
              {posts.length > 0 ? (
                <div className="space-y-8">
                  {posts.map(post => {
                    const postCity = extractCityFromPost(post);
                    return (
                      <BlogPostCard 
                        key={post.id} 
                        post={post} 
                        postCity={postCity} 
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
                  <div className="max-w-md mx-auto">
                    <div className="w-16 h-16 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="h-8 w-8 text-brand-trophy-gold" />
                    </div>
                    <h3 className="text-xl font-medium mb-2 text-brand-charcoal-gray">No articles found</h3>
                    <p className="text-gray-600">
                      No blog posts found for {selectedLocation}. Try selecting a different location or check back later.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <BlogSidebar 
              selectedLocation={selectedLocation}
              locations={locations}
              locationSearchQuery={locationSearchQuery}
              filteredLocations={filteredLocations}
              onLocationChange={handleLocationChange}
              onLocationSearchChange={setLocationSearchQuery}
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
